import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { queryClient } from '@/services/api/queryClient';
import { Text, Flex, useToast, Skeleton, Switch, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { GetParamsObject } from '@/model/query-params';
import { breadcrumb } from './breadcrumb';
import { WonTitle } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { WonTitleFilterBox } from './components/WonTitleFilterBox';
import { WonTitleTable } from './components/WonTitleTable/WonTitleTable';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { WonTitleFilters } from './types';
import { useDeleteWonTitleMutation, useGetWonTitleById, useWonTitle } from '@/services/queries/won-title';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { CompetitionEditModal } from './components/CompetitionEditModal';
import { ModalFormData as EditCompetitionModalFormData } from './components/CompetitionEditModal/schemas';
import { formatArrayToMultipleWordsSeparatedByComma } from '@/utils/string';
import { useCompetition, usePostCompetitionMutation } from '@/services/queries/competition';
import { WonTitleCreateModal } from './components/WonTitleCreateModal';
import { WonTitleEditModal } from './components/WonTitleEditModal';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { useTranslation } from 'react-i18next';
import { useAllClub } from '@/services/queries/club';

export const ApplicationWonTitle = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.WON_TITLE);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_won_title.application_won_title_enable_success')
      : t('pages.application_won_title.application_won_title_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_won_title.application_won_title_enable_error')
      : t('pages.application_won_title.application_won_title_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.WON_TITLE, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isOpenEditCompetitionModal, setIsOpenEditCompetitionModal] = useState(false);

  const [selectedItem, setSelectedItem] = useState<WonTitle | undefined>(undefined);

  const { mutateAsync: createCompetition, isLoading: isLoadingCreateCompetition } = usePostCompetitionMutation();
  const deleteMutation = useDeleteWonTitleMutation();
  const getItemByUUID = useGetWonTitleById();

  const { data: competitions, refetch: refetchCompetitions } = useCompetition();
  competitions?.sort((a, b) => a.name.localeCompare(b.name));

  const filtersForm = useFiltersForm<WonTitleFilters>({
    defaultValues: {
      team_uuid: '',
      competition_uuid: '',
      year: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: clubs = [] } = useAllClub(true);

  const { data: itemData, isLoading, forceRefetch } = useWonTitle(params);

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: WonTitle) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_won_title.application_won_title_delete_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_won_title.application_won_title_delete_error'),
        });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_won_title.application_won_title_not_found_error'),
        });
      },
    });
  };

  const handleEditCompetition = async ({ names }: EditCompetitionModalFormData) => {
    if (!names) return;

    const response = await Promise.allSettled(
      names.map(async item => {
        const response = await createCompetition({ name: item.name });
        return response.name;
      }),
    );

    const successItems = response
      ?.filter((item): item is PromiseFulfilledResult<string> => item.status === 'fulfilled')
      .map(item => item.value);

    const errorItems = response
      ?.filter((item): item is PromiseRejectedResult => item.status === 'rejected')
      .map(error => error.reason);

    if (errorItems.length > 0) {
      const positionsText =
        errorItems.length === 1
          ? t('pages.application_won_title.application_won_title_save_competition_error_singular')
          : t('pages.application_won_title.application_won_title_save_competition_error_plural');
      const errorsText =
        errorItems.length === 1 ? ` ${errorItems[0]}` : `: ${formatArrayToMultipleWordsSeparatedByComma(errorItems)}`;

      toast({
        title: `${positionsText}${errorsText}`,
        status: 'error',
      });

      await queryClient.invalidateQueries(['GET_COMPETITION']);

      return;
    }

    if (successItems?.length > 0) {
      const successText =
        successItems.length === 1
          ? t('pages.application_won_title.application_won_title_save_competition_success_singular')
          : t('pages.application_won_title.application_won_title_save_competition_success_plural');

      toast({
        title: successText,
        status: 'success',
      });

      setIsOpenEditCompetitionModal(false);
      refetchCompetitions();

      await queryClient.invalidateQueries(['GET_COMPETITION']);
    }
  };

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_won_title.application_won_title_page_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_won_title.application_won_title_label')}
                </Text>
                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_won_title.application_won_title_enabled')
                    : t('pages.application_won_title.application_won_title_disabled')}
                </Switch>
              </>
            )}
          </Flex>

          <Flex gap={2} direction={{ base: 'column', md: 'row' }}>
            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => {
                setIsCreateModalOpen(true);
              }}
              width={{ base: '100%', md: 'initial' }}
            >
              <ChakraFeatherIcon name='plus-circle' />
              {t('pages.application_won_title.application_won_title_create_button')}
            </Button>

            <Button width={{ base: '100%', md: 'initial' }} onClick={() => setIsOpenEditCompetitionModal(true)}>
              {t('pages.application_won_title.application_won_title_edit_competitions_button')}
            </Button>
          </Flex>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <WonTitleFilterBox clubs={clubs} filtersForm={filtersForm} forceRefetch={forceRefetch} competitions={competitions} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <WonTitleTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => onHandleEdit(item.uuid)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <WonTitleEditModal
          isOpen={!!isEditModalOpen}
          competitions={competitions}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <WonTitleCreateModal
        isOpen={!!isCreateModalOpen}
        competitions={competitions}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <CompetitionEditModal
        isOpen={isOpenEditCompetitionModal}
        competitions={competitions}
        onConfirm={handleEditCompetition}
        onClose={() => setIsOpenEditCompetitionModal(false)}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_won_title.application_won_title_delete_modal_title')}
        description={
          <Text>
            {t('pages.application_won_title.application_won_title_delete_confirm')}{' '}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.competition_name} - {selectedItem?.year}"
            </Text>
            ?
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_won_title.application_won_title_app_area_modal_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_won_title.application_won_title_app_area_confirm')
              : t('pages.application_won_title.application_won_title_app_area_disabled')}
          </Text>
        }
      />

      <CenteredSpinner
        isLoading={
          getItemByUUID.isLoading || isLoadingEnableOrDisableAppArea || deleteMutation.isLoading || isLoadingCreateCompetition
        }
      />
    </PageContent>
  );
};
